import { useState, useEffect, useRef } from 'react'
import {
   Button,
   Checkbox,
   ContentCard,
   Grid,
   Group,
   Radio,
   Stack,
} from '@forwardflow/ui-v2'
import cn from 'classnames'
import { ArrowLeftMdIcon, ChevronRightIcon } from '@forwardflow/icons'
import { IFilterCriteria, TLeadType } from '../interfaces'
import { PRIMARY_FILTER_OPTIONS, CAMPAIGN_TYPE_OPTIONS } from '../constants'
import { useCampaignFilterContext } from '../hooks'
import { FilterDefinition } from './FilterDefinition'
import { FilterRow } from './FilterRow'
import { KindsOfLeads } from './KindsOfLeads'
import { PrimaryFilter } from './PrimaryFilter'
import { FilterConfirmation } from './FilterConfirmation'

export const ConfigurationSteps = () => {
   const {
      step,
      setStep,
      getFirstFilterCriteria,
      getRestFilterCriterias,
      updateConfiguration,
      configuration,
      onNextStep,
      onSave,
      submitLabel,
   } = useCampaignFilterContext()

   const firstFilterCriteria = getFirstFilterCriteria()
   const existingFilterCriterias = getRestFilterCriterias()
   const [selectedFilterId, setSelectedFilterId] = useState<string | null>(null)
   const [shouldAddMoreFilters, setShouldAddMoreFilters] = useState<boolean>(false)

   const [filterCriterias, setFilterCriterias] = useState<IFilterCriteria[]>([])
   const hasInitializedFilters = useRef(false)

   // Initialize state when campaign configuration is loaded
   useEffect(() => {
      if (firstFilterCriteria?.field && !selectedFilterId) {
         setSelectedFilterId(firstFilterCriteria.field)
      }
   }, [firstFilterCriteria?.field, selectedFilterId])

   // Remove additional filters that clash with the primary filter field
   useEffect(() => {
      if (firstFilterCriteria?.field) {
         setFilterCriterias((prev) =>
            prev.filter((c) => c.field !== firstFilterCriteria.field)
         )
      }
   }, [firstFilterCriteria?.field])

   useEffect(() => {
      if (
         !hasInitializedFilters.current &&
         existingFilterCriterias.length > 0
      ) {
         hasInitializedFilters.current = true
         setShouldAddMoreFilters(true)
         setFilterCriterias(existingFilterCriterias as IFilterCriteria[])
      }
   }, [existingFilterCriterias])

   // Get fields that are already used (including the primary filter)
   const getUsedFields = () => {
      const usedFields = new Set<string>()
      if (firstFilterCriteria?.field) {
         usedFields.add(firstFilterCriteria.field)
      }
      filterCriterias.forEach((criteria) => {
         if (criteria.field) {
            usedFields.add(criteria.field)
         }
      })
      return usedFields
   }

   // Get available fields for a new filter
   const getAvailableFields = () => {
      const usedFields = getUsedFields()
      return PRIMARY_FILTER_OPTIONS.filter(
         (option) => !usedFields.has(option.value)
      )
   }

   // Add a new filter
   const handleAddFilter = () => {
      const availableFields = getAvailableFields()
      if (availableFields.length === 0) return

      const newFilter: IFilterCriteria = {
         field: availableFields[0].value as TLeadType,
         value: [],
         logic: 'AND',
         operator: 'CONTAINS_ANY',
      }
      setFilterCriterias([...filterCriterias, newFilter])
   }

   // Remove a filter
   const handleRemoveFilter = (index: number) => {
      setFilterCriterias(filterCriterias.filter((_, i) => i !== index))
   }

   // Update a filter
   const handleUpdateFilter = (index: number, filter: IFilterCriteria) => {
      const updatedFilters = [...filterCriterias]
      updatedFilters[index] = filter
      setFilterCriterias(updatedFilters)
   }

   // Handle adding more filters toggle
   const handleAddMoreFiltersToggle = (value: boolean) => {
      setShouldAddMoreFilters(value)
      if (value && filterCriterias.length === 0) {
         // Add a default filter when toggling to "Yes"
         const availableFields = getAvailableFields()
         if (availableFields.length > 0) {
            const newFilter: IFilterCriteria = {
               field: availableFields[0].value as TLeadType,
               value: [],
               logic: 'AND',
               operator: 'CONTAINS_ANY',
            }
            setFilterCriterias([newFilter])
         }
      }
   }

   // Save filters and continue
   const handleContinue = () => {
      // Only save valid filters (with selected values)
      const validFilters = filterCriterias.filter(
         (criteria) => criteria.value.length > 0
      )

      const nextConfiguration = shouldAddMoreFilters && validFilters.length > 0
         ? {
              campaign_filters: {
                 eligible_criteria: [
                    firstFilterCriteria as IFilterCriteria,
                    ...validFilters,
                 ],
              },
           }
         : {
              campaign_filters: {
                 eligible_criteria: [firstFilterCriteria as IFilterCriteria],
              },
           }

      if (shouldAddMoreFilters && validFilters.length > 0) {
         updateConfiguration(nextConfiguration)
      } else {
         // If no additional filters, just keep the primary filter
         updateConfiguration(nextConfiguration)
      }
      onSave?.({
         ...configuration,
         ...nextConfiguration,
      })
      onNextStep?.()
   }

   return (
      <Stack gap={32}>
         {step === 1 && (
            <>
               <KindsOfLeads showFilterSummary={false} />
               <Grid>
                  <Grid.Col span={12}>
                     <Button
                        className="w-full"
                        onClick={() => {
                           // If filters already configured, skip to last step
                           if (firstFilterCriteria?.field && firstFilterCriteria?.value?.length > 0) {
                              setStep(4)
                           } else {
                              setStep(2)
                           }
                        }}
                        disabled={!configuration?.campaign_type?.length}
                     >
                        Continue <ChevronRightIcon width={18} />
                     </Button>
                  </Grid.Col>
               </Grid>
            </>
         )}
         {step === 2 && (
            <PrimaryFilter
               setStep={setStep}
               selectedPrimaryFilter={selectedFilterId}
               setSelectedPrimaryFilter={setSelectedFilterId}
            />
         )}
         {step === 3 && (
            <FilterConfirmation
               setStep={setStep}
               selectedFilterId={selectedFilterId as TLeadType}
            />
         )}
         {step === 4 && (
            <>
               {/* Campaign Type Summary */}
               {(configuration?.campaign_type?.length ?? 0) > 0 && (
                  <ContentCard className="!py-4 !px-5">
                     <Group justify="space-between" align="center">
                        <Group gap={10}>
                           <span className="text-white/50 text-xs">Campaign Type:</span>
                           <Group gap={6}>
                              {configuration.campaign_type?.map((typeId) => {
                                 const opt = CAMPAIGN_TYPE_OPTIONS.find(o => o.id === typeId)
                                 return opt ? (
                                    <Group key={typeId} gap={4} className="bg-white/5 rounded px-2 py-1">
                                       <span className="shrink-0">{opt.icon}</span>
                                       <span className="text-white text-xs font-semibold">{opt.label}</span>
                                    </Group>
                                 ) : null
                              })}
                           </Group>
                        </Group>
                        <button
                           className="text-[#A684FF] text-xs hover:underline"
                           onClick={() => setStep(1)}
                        >
                           Change
                        </button>
                     </Group>
                  </ContentCard>
               )}

               <ContentCard className="!p-0">
                  <div className="p-6 border-b border-[#272E35] space-y-4">
                     <div>
                        <p className="text-white font-semibold text-lg mb-1">
                           Your primary filter
                        </p>
                        <p className="text-white/50 text-sm">
                           Leads matching these criteria will be prioritized.
                        </p>
                     </div>
                     <FilterDefinition
                        filterCriteria={firstFilterCriteria as IFilterCriteria}
                     />
                  </div>
                  {/* Lead Type Filter */}
                  <div className="p-6 border-b border-[#272E35] space-y-4">
                     <div>
                        <p className="text-white font-semibold text-lg mb-1">
                           What type of leads should this campaign target?
                        </p>
                        <p className="text-white/50 text-sm">
                           Choose whether to focus on buyers, sellers, rentals, or any mix of them.
                        </p>
                     </div>
                     <Group gap={12}>
                        {[
                           { id: 'buyer', label: 'Buyers' },
                           { id: 'seller', label: 'Sellers' },
                           { id: 'rental', label: 'Rentals' },
                        ].map((option) => {
                           const selectedLeadTypes = configuration?.lead_type_filter || ['buyer', 'seller']
                           const isSelected = selectedLeadTypes.includes(option.id)
                           return (
                              <button
                                 key={option.id}
                                 className={cn(
                                    'rounded-lg border px-4 py-2.5 transition-colors flex items-center gap-2',
                                    isSelected
                                       ? 'bg-[#A684FF]/10 border-[#A684FF]/40'
                                       : 'bg-white/[0.02] border-white/[0.1] hover:bg-white/[0.05]'
                                 )}
                                 onClick={() => {
                                    let newFilter: string[]
                                    if (isSelected) {
                                       newFilter = selectedLeadTypes.filter((t) => t !== option.id)
                                       if (newFilter.length === 0) return
                                    } else {
                                       newFilter = [...selectedLeadTypes, option.id]
                                    }
                                    updateConfiguration({ lead_type_filter: newFilter })
                                 }}
                              >
                                 <Checkbox readOnly checked={isSelected} className="pointer-events-none" />
                                 <p className="text-white text-sm font-medium">
                                    {option.label}
                                 </p>
                              </button>
                           )
                        })}
                     </Group>
                  </div>

                  <div className="p-6 space-y-5">
                     <div>
                        <p className="text-white font-semibold text-lg mb-1">
                           Do you want additional filtering?
                        </p>
                        <p className="text-white/50 text-sm">
                           Add extra layers to narrow down which leads Forward
                           Flow should work on.
                        </p>
                     </div>
                     <Group gap={12}>
                        <button
                           className={cn(
                              'rounded-lg border px-4 py-2 transition-colors',
                              shouldAddMoreFilters
                                 ? 'bg-[#A684FF]/10 border-[#A684FF]/40'
                                 : 'bg-white/[0.02] border-white/[0.1] hover:bg-white/[0.05]'
                           )}
                           onClick={() => handleAddMoreFiltersToggle(true)}
                        >
                           <Group gap={8}>
                              <Radio readOnly checked={shouldAddMoreFilters} />
                              <p className="text-white text-sm font-medium">
                                 Yes
                              </p>
                           </Group>
                        </button>
                        <button
                           className={cn(
                              'rounded-lg border px-4 py-2 transition-colors',
                              !shouldAddMoreFilters
                                 ? 'bg-[#A684FF]/10 border-[#A684FF]/40'
                                 : 'bg-white/[0.02] border-white/[0.1] hover:bg-white/[0.05]'
                           )}
                           onClick={() => handleAddMoreFiltersToggle(false)}
                        >
                           <Group gap={8}>
                              <Radio readOnly checked={!shouldAddMoreFilters} />
                              <p className="text-white text-sm font-medium">
                                 No
                              </p>
                           </Group>
                        </button>
                     </Group>
                     {shouldAddMoreFilters && (
                        <Stack gap={12}>
                           {filterCriterias.map((filterCriteria, index) => (
                              <FilterRow
                                 key={index}
                                 filterCriteria={filterCriteria}
                                 onUpdate={(filter) =>
                                    handleUpdateFilter(index, filter)
                                 }
                                 onRemove={() => handleRemoveFilter(index)}
                                 usedFields={getUsedFields()}
                              />
                           ))}
                           <button
                              className="w-full rounded-lg border border-dashed border-white/[0.15] hover:border-white/30 bg-transparent py-2.5 text-white/50 hover:text-white/70 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                              onClick={handleAddFilter}
                              disabled={getAvailableFields().length === 0}
                           >
                              <span className="text-base leading-none">+</span>
                              Add Filter
                           </button>
                        </Stack>
                     )}
                  </div>
               </ContentCard>
               <Grid>
                  <Grid.Col span={3}>
                     <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => setStep(3)}
                     >
                        <ArrowLeftMdIcon width={12} /> Back
                     </Button>
                  </Grid.Col>
                  <Grid.Col span={9}>
                     <Button className="w-full" onClick={handleContinue}>
                        {submitLabel} <ChevronRightIcon width={18} />
                     </Button>
                  </Grid.Col>
               </Grid>
            </>
         )}
      </Stack>
   )
}
